/**
 * CompetitionDashboard - Dashboard Analyse Concurrentielle
 *
 * Sections:
 * 1. Vue globale - KPIs par canal (Search, Shopping, PMAX)
 * 2. Évolution IS - Graphique tendance avec granularité
 * 3. Tableaux par canal - Détail campagnes
 * 4. Protection marque - Campagnes marque/concurrents
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp,
  Search,
  ShoppingCart,
  Zap,
  Target,
  Users,
  Shield,
  AlertCircle,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Eye,
  MousePointer,
  Monitor,
  Settings,
  Save,
  RotateCcw,
  X,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import './CompetitionDashboard.css';
import config from '../config';
import googleAdsLogo from '../logos/google-ads.png';
import DashboardLoadingScreen from './DashboardLoadingScreen';

const API_URL = config.API_URL;

// Couleurs pour les canaux
const CHANNEL_COLORS = {
  ALL: '#6366f1',
  SEARCH: '#4285f4',
  SHOPPING: '#34a853',
  PERFORMANCE_MAX: '#ea4335',
  DISPLAY: '#fbbc04',
  DEMAND_GEN: '#9333ea',
  DISCOVERY: '#9333ea',
  VIDEO: '#ff0000',
};

const CHANNEL_ICONS = {
  ALL: BarChart3,
  SEARCH: Search,
  SHOPPING: ShoppingCart,
  PERFORMANCE_MAX: Zap,
  DISPLAY: Monitor,
  DEMAND_GEN: Users,
  DISCOVERY: Users,
  VIDEO: Eye,
};

const CHANNEL_LABELS = {
  ALL: 'Tout',
  SEARCH: 'Search',
  SHOPPING: 'Shopping',
  PERFORMANCE_MAX: 'Performance Max',
  DISPLAY: 'Display',
  DEMAND_GEN: 'Demand Gen',
  DISCOVERY: 'Discovery',
  VIDEO: 'Video',
};

// Styles de badges par type de canal (comme BidOptimizer)
const CHANNEL_BADGE_STYLES = {
  ALL: { label: 'TOUT', bg: '#f3f4f6', color: '#374151' },
  SEARCH: { label: 'SEARCH', bg: '#dbeafe', color: '#1e40af' },
  SHOPPING: { label: 'SHOPPING', bg: '#fef3c7', color: '#92400e' },
  PERFORMANCE_MAX: { label: 'PMAX', bg: '#e0e7ff', color: '#3730a3' },
  DISPLAY: { label: 'DISPLAY', bg: '#fce7f3', color: '#9d174d' },
  DEMAND_GEN: { label: 'DEMAND GEN', bg: '#f3e8ff', color: '#7c3aed' },
};

// Clé localStorage pour les mots-clés
const KEYWORDS_STORAGE_KEY = 'competition_brand_keywords';

// Charger les mots-clés depuis localStorage
const loadKeywordsFromStorage = () => {
  try {
    const stored = localStorage.getItem(KEYWORDS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Erreur chargement mots-clés:', e);
  }
  return null;
};

// Sauvegarder les mots-clés dans localStorage
const saveKeywordsToStorage = (keywords) => {
  try {
    localStorage.setItem(KEYWORDS_STORAGE_KEY, JSON.stringify(keywords));
  } catch (e) {
    console.warn('Erreur sauvegarde mots-clés:', e);
  }
};

const CompetitionDashboard = ({ token, globalConnectors, selectedPeriod = 'LAST_30_DAYS', selectedClientId, selectedAccount: parentSelectedAccount }) => {
  // Get sourceId from parent selected account
  const sourceId = parentSelectedAccount?.sourceId || parentSelectedAccount?.id;
  const accountName = parentSelectedAccount?.name || 'Compte';

  // State - Data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState([]);
  const [trend, setTrend] = useState([]);
  const [campaigns, setCampaigns] = useState({ ALL: null, SEARCH: null, SHOPPING: null, PERFORMANCE_MAX: null });
  const [brand, setBrand] = useState(null);
  const [brandTrend, setBrandTrend] = useState([]);
  const [auctionInsightsByCampaign, setAuctionInsightsByCampaign] = useState([]);
  const [activeChannel, setActiveChannel] = useState('ALL');
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    trend: true,
    campaigns: true,
    brand: true,
    auctionInsights: true,
  });

  // State - Filtre par campagne
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState(null);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const [showCampaignAutocomplete, setShowCampaignAutocomplete] = useState(false);

  // State - Granularité du trend
  const [trendGranularity, setTrendGranularity] = useState('day');

  // Calculer la granularité par défaut selon la période
  // Aligné exactement sur les périodes du dashboard global Google Ads
  const getDefaultGranularity = useCallback((period) => {
    const today = new Date();

    // Calculer le nombre de jours réel pour les périodes dynamiques
    let days;
    switch (period) {
      case 'LAST_7_DAYS':
        days = 7;
        break;
      case 'LAST_14_DAYS':
        days = 14;
        break;
      case 'LAST_30_DAYS':
        days = 30;
        break;
      case 'LAST_90_DAYS':
        days = 90;
        break;
      case 'LAST_180_DAYS':
        days = 180;
        break;
      case 'LAST_365_DAYS':
        days = 365;
        break;
      case 'THIS_MONTH':
        // Nombre de jours écoulés dans le mois en cours
        days = today.getDate();
        break;
      case 'LAST_MONTH':
        // Nombre de jours du mois précédent
        days = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
        break;
      case 'THIS_YEAR':
        // Nombre de jours écoulés depuis le 1er janvier
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        days = Math.floor((today - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
        break;
      case 'LAST_YEAR':
        days = 365;
        break;
      default:
        days = 30;
    }

    if (days <= 45) return 'day';
    if (days <= 120) return 'week';
    return 'month';
  }, []);

  // State - Configuration mots-clés
  const [showKeywordsConfig, setShowKeywordsConfig] = useState(false);
  const [defaultKeywords, setDefaultKeywords] = useState({ brand: [], competitor: [] });
  const [customBrandKeywords, setCustomBrandKeywords] = useState('');
  const [customCompetitorKeywords, setCustomCompetitorKeywords] = useState('');
  const [useCustomKeywords, setUseCustomKeywords] = useState(false);

  // Charger les mots-clés au montage
  useEffect(() => {
    const stored = loadKeywordsFromStorage();
    if (stored) {
      setCustomBrandKeywords(stored.brand || '');
      setCustomCompetitorKeywords(stored.competitor || '');
      setUseCustomKeywords(stored.useCustom || false);
    }
  }, []);

  // Charger les mots-clés par défaut depuis l'API
  useEffect(() => {
    const fetchDefaultKeywords = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/dashboard/competition/default-keywords`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          setDefaultKeywords(res.data.data);
        }
      } catch (err) {
        console.warn('Erreur chargement mots-clés par défaut:', err);
      }
    };
    if (token) {
      fetchDefaultKeywords();
    }
  }, [token]);

  // Construire les params pour les mots-clés
  const getKeywordsParams = useCallback(() => {
    if (useCustomKeywords) {
      const params = {};
      if (customBrandKeywords.trim()) {
        params.brandKeywords = customBrandKeywords.trim();
      }
      if (customCompetitorKeywords.trim()) {
        params.competitorKeywords = customCompetitorKeywords.trim();
      }
      return params;
    }
    return {};
  }, [useCustomKeywords, customBrandKeywords, customCompetitorKeywords]);

  // Sauvegarder les mots-clés
  const handleSaveKeywords = () => {
    saveKeywordsToStorage({
      brand: customBrandKeywords,
      competitor: customCompetitorKeywords,
      useCustom: useCustomKeywords,
    });
    setShowKeywordsConfig(false);
    // Recharger les données avec les nouveaux mots-clés
    fetchData();
  };

  // Réinitialiser aux valeurs par défaut
  const handleResetKeywords = () => {
    setCustomBrandKeywords('');
    setCustomCompetitorKeywords('');
    setUseCustomKeywords(false);
    saveKeywordsToStorage({
      brand: '',
      competitor: '',
      useCustom: false,
    });
  };

  // Fetch trend data only (appelé quand la granularité change)
  const fetchTrendData = useCallback(async (granularity) => {
    if (!sourceId) return;

    const headers = { Authorization: `Bearer ${token}` };
    const campaignIdParam = selectedCampaignFilter?.id || selectedCampaignFilter?.campaignId;

    try {
      const trendRes = await axios.get(`${API_URL}/api/dashboard/competition/trend`, {
        headers,
        params: {
          sourceId,
          dateRange: selectedPeriod,
          granularity,
          ...(campaignIdParam && { campaignId: campaignIdParam })
        },
      });
      setTrend(trendRes.data.data || []);
    } catch (err) {
      console.error('Erreur chargement trend:', err);
    }
  }, [sourceId, token, selectedPeriod, selectedCampaignFilter]);

  // Fetch all data (sans le trend qui est géré séparément)
  const fetchData = useCallback(async () => {
    if (!sourceId) return;

    setLoading(true);
    setError(null);

    const headers = { Authorization: `Bearer ${token}` };
    const campaignIdParam = selectedCampaignFilter?.id || selectedCampaignFilter?.campaignId;
    const baseParams = {
      sourceId,
      dateRange: selectedPeriod,
      ...(campaignIdParam && { campaignId: campaignIdParam })
    };
    const keywordsParams = getKeywordsParams();

    try {
      // Fetch overview (avec filtre campagne si sélectionnée)
      const overviewRes = await axios.get(`${API_URL}/api/dashboard/competition/overview`, {
        headers,
        params: baseParams,
      });
      setOverview(overviewRes.data.data || []);

      // Fetch trend avec granularité calculée selon la période
      const defaultGran = getDefaultGranularity(selectedPeriod);
      setTrendGranularity(defaultGran);
      const trendRes = await axios.get(`${API_URL}/api/dashboard/competition/trend`, {
        headers,
        params: { ...baseParams, granularity: defaultGran },
      });
      setTrend(trendRes.data.data || []);

      // Fetch campaigns for each channel (including ALL)
      // Note: On récupère TOUTES les campagnes pour le dropdown (sans filtre)
      // mais on applique le filtre campagne pour l'affichage
      const channelTypes = ['ALL', 'SEARCH', 'SHOPPING', 'PERFORMANCE_MAX'];

      // D'abord récupérer toutes les campagnes pour le dropdown (sans filtre)
      const allCampaignsRes = await axios.get(`${API_URL}/api/dashboard/competition/campaigns`, {
        headers,
        params: { sourceId, dateRange: selectedPeriod, channelType: 'ALL' },
      }).catch(() => ({ data: { data: null } }));

      if (allCampaignsRes.data?.data?.campaigns) {
        setAvailableCampaigns(allCampaignsRes.data.data.campaigns);
      }

      // Ensuite récupérer les campagnes par canal (avec filtre si sélectionné)
      const campaignParams = { sourceId, dateRange: selectedPeriod };
      if (selectedCampaignFilter?.id) {
        campaignParams.campaignId = selectedCampaignFilter.id;
      }

      const campaignPromises = channelTypes.map(channelType =>
        axios.get(`${API_URL}/api/dashboard/competition/campaigns`, {
          headers,
          params: { ...campaignParams, channelType },
        }).then(res => ({ channelType, data: res.data.data }))
          .catch(() => ({ channelType, data: null }))
      );

      const campaignResults = await Promise.all(campaignPromises);
      const campaignsData = {};
      campaignResults.forEach(r => {
        campaignsData[r.channelType] = r.data;
      });
      setCampaigns(campaignsData);

      // Fetch brand protection (avec mots-clés et filtre campagne)
      const brandParams = { sourceId, dateRange: selectedPeriod, ...keywordsParams };
      if (selectedCampaignFilter?.id) {
        brandParams.campaignId = selectedCampaignFilter.id;
      }
      const brandRes = await axios.get(`${API_URL}/api/dashboard/competition/brand`, {
        headers,
        params: brandParams,
      });
      setBrand(brandRes.data.data || null);

      // Fetch brand trend (avec mots-clés, période sélectionnée et filtre campagne)
      const brandTrendParams = { sourceId, dateRange: selectedPeriod, ...keywordsParams };
      if (selectedCampaignFilter?.id) {
        brandTrendParams.campaignId = selectedCampaignFilter.id;
      }
      const brandTrendRes = await axios.get(`${API_URL}/api/dashboard/competition/brand-trend`, {
        headers,
        params: brandTrendParams,
      });
      setBrandTrend(brandTrendRes.data.data || []);

      // Fetch auction insights by campaign (concurrents par campagne)
      try {
        const auctionRes = await axios.get(`${API_URL}/api/dashboard/competition/auction-insights-by-campaign`, {
          headers,
          params: { sourceId, dateRange: selectedPeriod },
        });
        setAuctionInsightsByCampaign(auctionRes.data.data || []);
      } catch (auctionErr) {
        console.warn('Auction Insights non disponible:', auctionErr.message);
        setAuctionInsightsByCampaign([]);
      }

    } catch (err) {
      console.error('Erreur chargement données concurrence:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [sourceId, token, selectedPeriod, getKeywordsParams, getDefaultGranularity, selectedCampaignFilter]);

  // Charger toutes les données au montage et quand les paramètres changent
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Recharger uniquement le trend quand la granularité change manuellement
  const handleGranularityChange = (newGranularity) => {
    setTrendGranularity(newGranularity);
    fetchTrendData(newGranularity);
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Format percentage
  const formatPercent = (value, decimals = 1) => {
    if (value === null || value === undefined) return '—';
    return `${(value * 100).toFixed(decimals)}%`;
  };

  // Format number
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString('fr-FR');
  };

  // Prepare trend chart data avec remplissage des dates manquantes
  const prepareTrendData = () => {
    if (!trend || trend.length === 0) return [];

    const periodMap = {};

    trend.forEach(item => {
      const periodKey = item.period || item.month;
      if (!periodMap[periodKey]) {
        periodMap[periodKey] = { period: periodKey };
      }
      periodMap[periodKey][item.channelType] = item.impressionShare ? item.impressionShare * 100 : null;
    });

    // Pour la granularité jour, remplir les dates manquantes
    if (trendGranularity === 'day') {
      const periods = Object.keys(periodMap).sort();
      if (periods.length >= 2) {
        // Parser les dates en UTC pour éviter les problèmes de timezone
        const [startYear, startMonth, startDay] = periods[0].split('-').map(Number);
        const [endYear, endMonth, endDay] = periods[periods.length - 1].split('-').map(Number);

        const startDate = new Date(startYear, startMonth - 1, startDay);
        const endDate = new Date(endYear, endMonth - 1, endDay);

        // Générer toutes les dates entre start et end
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, '0');
          const day = String(currentDate.getDate()).padStart(2, '0');
          const dateKey = `${year}-${month}-${day}`;

          if (!periodMap[dateKey]) {
            periodMap[dateKey] = { period: dateKey };
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    }

    return Object.values(periodMap).sort((a, b) => a.period.localeCompare(b.period));
  };

  // Prepare brand trend chart data
  const prepareBrandTrendData = () => {
    const periodMap = {};

    brandTrend.forEach(item => {
      const periodKey = item.period || item.month;
      if (!periodMap[periodKey]) {
        periodMap[periodKey] = { period: periodKey };
      }
      periodMap[periodKey][item.type] = item.impressionShare ? item.impressionShare * 100 : null;
    });

    return Object.values(periodMap).sort((a, b) => a.period.localeCompare(b.period));
  };

  // Format period label selon la granularité
  const formatPeriodLabel = (periodStr, granularity, forTooltip = false) => {
    if (!periodStr) return '';
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

    switch (granularity) {
      case 'day': {
        // Format: 2024-01-15 -> 15/01 (ou Lundi 15/01 pour tooltip)
        const [dayYear, dayMonth, dayDay] = periodStr.split('-');
        if (forTooltip) {
          const date = new Date(parseInt(dayYear), parseInt(dayMonth) - 1, parseInt(dayDay));
          const dayName = days[date.getDay()];
          return `${dayName} ${dayDay}/${dayMonth}`;
        }
        return `${dayDay}/${dayMonth}`;
      }

      case 'week': {
        // Format: 2024-W03 -> Sem 03
        if (periodStr.includes('-W')) {
          const weekNum = periodStr.split('-W')[1];
          return `Sem ${weekNum}`;
        }
        return periodStr;
      }

      case 'month': {
        // Format: 2024-01 -> Jan 24
        const [monthYear, month] = periodStr.split('-');
        return `${months[parseInt(month) - 1]} ${monthYear.slice(2)}`;
      }

      case 'quarter': {
        // Format: 2024-Q1 -> T1 24
        if (periodStr.includes('-Q')) {
          const [qYear, quarter] = periodStr.split('-Q');
          return `T${quarter} ${qYear.slice(2)}`;
        }
        return periodStr;
      }

      case 'year':
        // Format: 2024 -> 2024
        return periodStr;

      default:
        return periodStr;
    }
  };

  // No account available
  if (!sourceId) {
    return (
      <div className="competition-error">
        <AlertCircle size={48} />
        <p>Aucun compte sélectionné</p>
        <span className="hint">Sélectionnez un compte Google Ads pour accéder à l'analyse concurrentielle.</span>
      </div>
    );
  }

  // Loading state - afficher uniquement l'écran de chargement (même style que dashboard global)
  if (loading) {
    return (
      <DashboardLoadingScreen
        platformName="Google Ads"
        features={[
          { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
          { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
          { icon: <Target size={28} />, label: 'Analyse concurrentielle' }
        ]}
      />
    );
  }

  return (
    <div className="competition-dashboard">
      {/* Sticky Campaign Filter Banner - au dessus du titre */}
      {availableCampaigns.length > 0 && (
        <div style={{
          backgroundColor: '#f9fafb',
          padding: '16px 20px',
          borderRadius: '8px',
          marginTop: '-5px',
          marginBottom: '10px',
          border: '1px solid #e5e7eb',
          position: 'sticky',
          top: '0',
          zIndex: '1000',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{
                fontWeight: '500',
                color: '#374151',
                fontSize: '0.95em',
                display: 'flex',
                alignItems: 'center'
              }}>
                <img src={googleAdsLogo} alt="Google Ads" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
                Filtrer:
              </span>
              <div style={{ position: 'relative', width: '400px' }}>
                <input
                  type="text"
                  value={campaignSearchQuery}
                  onChange={(e) => {
                    setCampaignSearchQuery(e.target.value);
                    setShowCampaignAutocomplete(true);
                  }}
                  onFocus={() => setShowCampaignAutocomplete(true)}
                  placeholder={!selectedCampaignFilter
                    ? `Toutes les campagnes (${availableCampaigns.length})`
                    : selectedCampaignFilter.name || 'Rechercher une campagne...'}
                  style={{
                    padding: '4px 36px 4px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    fontSize: '0.9em',
                    color: '#374151',
                    cursor: 'text',
                    width: '100%',
                    height: '35px',
                    boxSizing: 'border-box'
                  }}
                />
                {(campaignSearchQuery || selectedCampaignFilter) && (
                  <button
                    onClick={() => {
                      setCampaignSearchQuery('');
                      setSelectedCampaignFilter(null);
                      setShowCampaignAutocomplete(false);
                    }}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#9ca3af',
                      fontSize: '1em',
                      padding: '4px'
                    }}
                  >
                    ✕
                  </button>
                )}
                {showCampaignAutocomplete && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1001
                  }}>
                    <div
                      onClick={() => {
                        setSelectedCampaignFilter(null);
                        setCampaignSearchQuery('');
                        setShowCampaignAutocomplete(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        backgroundColor: !selectedCampaignFilter ? '#FAF9F5' : 'transparent',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = !selectedCampaignFilter ? '#FAF9F5' : 'transparent'}
                    >
                      <strong>Toutes les campagnes ({availableCampaigns.length})</strong>
                    </div>
                    {[...availableCampaigns]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .filter(campaign =>
                        !campaignSearchQuery ||
                        (campaign.name || '').toLowerCase().includes(campaignSearchQuery.toLowerCase())
                      )
                      .map(campaign => (
                        <div
                          key={campaign.id}
                          onClick={() => {
                            setSelectedCampaignFilter(campaign);
                            setCampaignSearchQuery('');
                            setShowCampaignAutocomplete(false);
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            backgroundColor: selectedCampaignFilter?.id === campaign.id ? '#FAF9F5' : 'transparent',
                            borderBottom: '1px solid #f3f4f6'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = selectedCampaignFilter?.id === campaign.id ? '#FAF9F5' : 'transparent'}
                        >
                          <div style={{ fontWeight: '500' }}>{campaign.name}</div>
                          <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                            {campaign.channelType || 'Campagne'}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Bouton Analyse IA */}
            <button
              onClick={() => {
                if (window.competitionDashboardAIAnalysis) {
                  window.competitionDashboardAIAnalysis();
                } else if (window.googleDashboardAIAnalysis) {
                  window.googleDashboardAIAnalysis();
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '0.9em',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
              title="Analyse IA du dashboard concurrence"
            >
              🤖 Analyse IA
            </button>
          </div>

          {/* Indicateur de filtre actif */}
          {selectedCampaignFilter && (
            <div style={{
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '0.85em',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <img src={googleAdsLogo} alt="Google Ads" style={{ width: '20px', height: '20px' }} />
                <span>{selectedCampaignFilter.name}</span>
                <button
                  onClick={() => setSelectedCampaignFilter(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#1e40af'
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de configuration des mots-clés */}
      {showKeywordsConfig && (
        <div className="keywords-config-overlay" onClick={() => setShowKeywordsConfig(false)}>
          <div className="keywords-config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Settings size={20} /> Configuration des mots-clés</h3>
              <button className="close-btn" onClick={() => setShowKeywordsConfig(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p className="config-description">
                Personnalisez les mots-clés utilisés pour détecter les campagnes de marque et de concurrents.
                Séparez les mots-clés par des virgules.
              </p>

              <div className="keywords-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={useCustomKeywords}
                    onChange={(e) => setUseCustomKeywords(e.target.checked)}
                  />
                  <span className="toggle-text">Utiliser des mots-clés personnalisés</span>
                </label>
              </div>

              {useCustomKeywords ? (
                <div className="keywords-inputs">
                  <div className="keyword-group">
                    <label>
                      <Shield size={16} /> Mots-clés Marque
                    </label>
                    <textarea
                      value={customBrandKeywords}
                      onChange={(e) => setCustomBrandKeywords(e.target.value)}
                      placeholder="marque, brand, branded, nom de votre marque..."
                      rows={3}
                    />
                    <span className="hint">Campagnes contenant ces mots = Campagnes Marque</span>
                  </div>

                  <div className="keyword-group">
                    <label>
                      <Users size={16} /> Mots-clés Concurrents
                    </label>
                    <textarea
                      value={customCompetitorKeywords}
                      onChange={(e) => setCustomCompetitorKeywords(e.target.value)}
                      placeholder="concurrent, competitor, conquête, conquest..."
                      rows={3}
                    />
                    <span className="hint">Campagnes contenant ces mots = Campagnes Concurrents</span>
                  </div>
                </div>
              ) : (
                <div className="default-keywords-info">
                  <div className="default-group">
                    <h4><Shield size={16} /> Mots-clés Marque par défaut ({defaultKeywords.brand?.length || 0})</h4>
                    <div className="keywords-tags">
                      {defaultKeywords.brand?.slice(0, 15).map((kw, i) => (
                        <span key={i} className="keyword-tag brand">{kw}</span>
                      ))}
                      {defaultKeywords.brand?.length > 15 && (
                        <span className="keyword-tag more">+{defaultKeywords.brand.length - 15} autres</span>
                      )}
                    </div>
                  </div>

                  <div className="default-group">
                    <h4><Users size={16} /> Mots-clés Concurrents par défaut ({defaultKeywords.competitor?.length || 0})</h4>
                    <div className="keywords-tags">
                      {defaultKeywords.competitor?.slice(0, 15).map((kw, i) => (
                        <span key={i} className="keyword-tag competitor">{kw}</span>
                      ))}
                      {defaultKeywords.competitor?.length > 15 && (
                        <span className="keyword-tag more">+{defaultKeywords.competitor.length - 15} autres</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="reset-btn" onClick={handleResetKeywords}>
                <RotateCcw size={16} /> Réinitialiser
              </button>
              <div className="footer-right">
                <button className="cancel-btn" onClick={() => setShowKeywordsConfig(false)}>
                  Annuler
                </button>
                <button className="save-btn" onClick={handleSaveKeywords}>
                  <Save size={16} /> Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="competition-error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={fetchData}>Réessayer</button>
        </div>
      )}

      {/* No data state */}
      {!loading && !error && overview.length === 0 && (
        <div className="competition-no-data">
          <AlertCircle size={48} />
          <p>Aucune donnée disponible pour ce compte</p>
          <span className="hint">Sélectionnez un autre compte ou vérifiez que des campagnes sont actives.</span>
        </div>
      )}

      {/* Section 1: Trend - Évolution IS */}
      <section className="competition-section">
        <div className="section-header" onClick={() => toggleSection('trend')}>
          <div className="section-header-left">
            <h2><TrendingUp size={20} /> Évolution de la Part d'Impressions</h2>
          </div>
          {/* Boutons de granularité */}
          <div className="granularity-buttons" onClick={(e) => e.stopPropagation()}>
            {[
              { value: 'day', label: 'Jour' },
              { value: 'week', label: 'Semaine' },
              { value: 'month', label: 'Mois' },
              { value: 'quarter', label: 'Trimestre' },
              { value: 'year', label: 'Année' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => handleGranularityChange(value)}
                className={`granularity-btn ${trendGranularity === value ? 'active' : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
          {expandedSections.trend ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>

        {expandedSections.trend && (
          <div className="section-content">
            <div className="trend-chart">
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={prepareTrendData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="period"
                    tickFormatter={(value) => formatPeriodLabel(value, trendGranularity)}
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    formatter={(value) => value ? `${value.toFixed(1)}%` : '—'}
                    labelFormatter={(value) => formatPeriodLabel(value, trendGranularity, true)}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="SEARCH"
                    name="Search"
                    stroke={CHANNEL_COLORS.SEARCH}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="SHOPPING"
                    name="Shopping"
                    stroke={CHANNEL_COLORS.SHOPPING}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="PERFORMANCE_MAX"
                    name="Performance Max"
                    stroke={CHANNEL_COLORS.PERFORMANCE_MAX}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </section>

      {/* Section 2: Overview - KPIs par canal */}
      <section className="competition-section">
        <div className="section-header" onClick={() => toggleSection('overview')}>
          <h2><BarChart3 size={20} /> Vue Globale - Part d'Impressions par Canal</h2>
          {expandedSections.overview ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>

        {expandedSections.overview && (
          <div className="section-content">
            <div className="channel-cards">
              {/* Filtrer uniquement les canaux où IS est pertinent: Search, Shopping, PMAX */}
              {overview
                .filter(channel => ['SEARCH', 'SHOPPING', 'PERFORMANCE_MAX'].includes(channel.channelType))
                .map(channel => {
                const Icon = CHANNEL_ICONS[channel.channelType] || Target;
                const color = CHANNEL_COLORS[channel.channelType] || '#6b7280';
                const label = CHANNEL_LABELS[channel.channelType] || channel.channelType;

                const impressionShare = channel.searchImpressionShare;
                const rankLostIS = channel.searchRankLostIS;
                const budgetLostIS = channel.searchBudgetLostIS;
                const topIS = channel.searchTopIS;
                const absTopIS = channel.searchAbsoluteTopIS;

                return (
                  <div
                    key={channel.channelType}
                    className="channel-card"
                    style={{ '--channel-color': color }}
                  >
                    <div className="channel-card-header">
                      <Icon size={24} style={{ color }} />
                      <span className="channel-label">{label}</span>
                    </div>

                    <div className="channel-main-metric">
                      <span className="metric-value">{formatPercent(impressionShare)}</span>
                      <span className="metric-label">Part d'Impressions</span>
                    </div>

                    <div className="channel-metrics-grid">
                      <div className="channel-metric">
                        <span className="metric-label">Top Page</span>
                        <span className="metric-value">{formatPercent(topIS)}</span>
                      </div>
                      <div className="channel-metric">
                        <span className="metric-label">Abs. Top</span>
                        <span className="metric-value">{formatPercent(absTopIS)}</span>
                      </div>
                      <div className="channel-metric negative">
                        <span className="metric-label">Perte Rang</span>
                        <span className="metric-value">{formatPercent(rankLostIS)}</span>
                      </div>
                      <div className="channel-metric negative">
                        <span className="metric-label">Perte Budget</span>
                        <span className="metric-value">{formatPercent(budgetLostIS)}</span>
                      </div>
                    </div>

                    <div className="channel-performance">
                      <div className="perf-item">
                        <Eye size={14} />
                        <span>{formatNumber(channel.impressions)} impr.</span>
                      </div>
                      <div className="perf-item">
                        <MousePointer size={14} />
                        <span>{formatNumber(channel.clicks)} clics</span>
                      </div>
                      <div className="perf-item">
                        <span className="roas-badge">ROAS {channel.roas?.toFixed(2) || '—'}</span>
                      </div>
                    </div>

                    {/* Progress bars for lost IS */}
                    <div className="is-loss-bars">
                      <div className="loss-bar">
                        <span className="loss-label">Perte Rang</span>
                        <div className="loss-progress">
                          <div
                            className="loss-fill rank"
                            style={{ width: `${Math.min((rankLostIS || 0) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="loss-value">{formatPercent(rankLostIS)}</span>
                      </div>
                      <div className="loss-bar">
                        <span className="loss-label">Perte Budget</span>
                        <div className="loss-progress">
                          <div
                            className="loss-fill budget"
                            style={{ width: `${Math.min((budgetLostIS || 0) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="loss-value">{formatPercent(budgetLostIS)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Section 3: Campaigns by channel */}
      <section className="competition-section">
        <div className="section-header" onClick={() => toggleSection('campaigns')}>
          <h2><Search size={20} /> Détail par Canal</h2>
          {expandedSections.campaigns ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>

        {expandedSections.campaigns && (
          <div className="section-content">
            {/* Channel tabs - style badges comme BidOptimizer */}
            <div className="channel-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {['ALL', 'SEARCH', 'SHOPPING', 'PERFORMANCE_MAX'].map(channel => {
                const badgeStyle = CHANNEL_BADGE_STYLES[channel];
                const isActive = activeChannel === channel;
                return (
                  <button
                    key={channel}
                    onClick={() => setActiveChannel(channel)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      backgroundColor: badgeStyle.bg,
                      color: badgeStyle.color,
                      border: isActive ? `2px solid ${badgeStyle.color}` : '2px solid transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                      transform: isActive ? 'scale(1.05)' : 'scale(1)'
                    }}
                  >
                    {badgeStyle.label}
                  </button>
                );
              })}
            </div>

            {/* Campaign table */}
            {campaigns[activeChannel] && (
              <div className="campaigns-table-wrapper">
                {/* Summary row - afficher uniquement les métriques pertinentes selon le canal */}
                <div className="campaigns-summary">
                  <div className="summary-item">
                    <span className="summary-label">IS Moyen</span>
                    <span className="summary-value">{formatPercent(campaigns[activeChannel].summary?.impressionShare)}</span>
                  </div>
                  {/* Top IS et Abs. Top IS uniquement pour Search */}
                  {activeChannel === 'SEARCH' && (
                    <>
                      <div className="summary-item">
                        <span className="summary-label">Top IS</span>
                        <span className="summary-value">{formatPercent(campaigns[activeChannel].summary?.topIS)}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Abs. Top IS</span>
                        <span className="summary-value">{formatPercent(campaigns[activeChannel].summary?.absoluteTopIS)}</span>
                      </div>
                    </>
                  )}
                  <div className="summary-item negative">
                    <span className="summary-label">Perte Rang</span>
                    <span className="summary-value">{formatPercent(campaigns[activeChannel].summary?.rankLostIS)}</span>
                  </div>
                  <div className="summary-item negative">
                    <span className="summary-label">Perte Budget</span>
                    <span className="summary-value">{formatPercent(campaigns[activeChannel].summary?.budgetLostIS)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">ROAS</span>
                    <span className="summary-value">{campaigns[activeChannel].summary?.roas?.toFixed(2) || '—'}</span>
                  </div>
                </div>

                <table className="campaigns-table">
                  <thead>
                    <tr>
                      <th>Campagne</th>
                      {activeChannel === 'ALL' && <th>Canal</th>}
                      <th className="num">Impr.</th>
                      <th className="num">IS</th>
                      {activeChannel === 'SEARCH' && (
                        <>
                          <th className="num">Click Share</th>
                          <th className="num">Abs. Top</th>
                          <th className="num">Top IS</th>
                        </>
                      )}
                      <th className="num negative">Perte Rang</th>
                      <th className="num negative">Perte Budget</th>
                      <th className="num">ROAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns[activeChannel].campaigns
                      ?.filter(campaign => ['SEARCH', 'SHOPPING', 'PERFORMANCE_MAX'].includes(campaign.channelType))
                      ?.map(campaign => (
                      <tr key={campaign.id}>
                        <td className="campaign-name">{campaign.name}</td>
                        {activeChannel === 'ALL' && (
                          <td>
                            {(() => {
                              const badgeStyle = CHANNEL_BADGE_STYLES[campaign.channelType] || { label: '?', bg: '#f3f4f6', color: '#374151' };
                              return (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    borderRadius: '4px',
                                    backgroundColor: badgeStyle.bg,
                                    color: badgeStyle.color
                                  }}
                                >
                                  {badgeStyle.label}
                                </span>
                              );
                            })()}
                          </td>
                        )}
                        <td className="num">{formatNumber(campaign.impressions)}</td>
                        <td className="num">
                          <span className="is-badge">{formatPercent(campaign.impressionShare)}</span>
                        </td>
                        {activeChannel === 'SEARCH' && (
                          <>
                            <td className="num">{formatPercent(campaign.clickShare)}</td>
                            <td className="num">{formatPercent(campaign.absoluteTopIS)}</td>
                            <td className="num">{formatPercent(campaign.topIS)}</td>
                          </>
                        )}
                        <td className="num negative">{formatPercent(campaign.rankLostIS)}</td>
                        <td className="num negative">{formatPercent(campaign.budgetLostIS)}</td>
                        <td className="num">
                          <span className={`roas-value ${campaign.roas >= 3 ? 'good' : campaign.roas >= 1 ? 'ok' : 'bad'}`}>
                            {campaign.roas?.toFixed(2) || '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!campaigns[activeChannel] && (
              <div className="no-data">
                <p>Aucune donnée disponible pour ce canal</p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Section 4: Brand Protection */}
      <section className="competition-section">
        <div className="section-header" onClick={() => toggleSection('brand')}>
          <div className="section-header-left">
            <h2><Shield size={20} /> Protection de la Marque</h2>
          </div>
          <button
            className={`config-btn ${useCustomKeywords ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setShowKeywordsConfig(true); }}
            title="Configurer les mots-clés de détection"
          >
            <Settings size={16} />
            Mots-clés
            {useCustomKeywords && <span className="custom-badge">Personnalisé</span>}
          </button>
          {expandedSections.brand ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>

        {expandedSections.brand && brand && (
          <div className="section-content">
            <div className="brand-grid">
              {/* Brand campaigns card */}
              <div className="brand-card brand">
                <div className="brand-card-header">
                  <Shield size={24} />
                  <h3>Campagnes Marque</h3>
                </div>
                {brand.brand?.summary ? (
                  <>
                    <div className="brand-main-metric">
                      <span className="metric-value">{formatPercent(brand.brand.summary.avgImpressionShare)}</span>
                      <span className="metric-label">IS Moyen</span>
                    </div>
                    <div className="brand-metrics">
                      <div className="brand-metric">
                        <span className="label">Abs. Top IS</span>
                        <span className="value">{formatPercent(brand.brand.summary.avgAbsoluteTopIS)}</span>
                      </div>
                      <div className="brand-metric negative">
                        <span className="label">Perte Rang</span>
                        <span className="value">{formatPercent(brand.brand.summary.avgRankLostIS)}</span>
                      </div>
                      <div className="brand-metric">
                        <span className="label">Campagnes</span>
                        <span className="value">{brand.brand.summary.count}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="no-data-text">Aucune campagne marque détectée</p>
                )}
              </div>

              {/* Competitor campaigns card */}
              <div className="brand-card competitor">
                <div className="brand-card-header">
                  <Users size={24} />
                  <h3>Campagnes Concurrents</h3>
                </div>
                {brand.competitor?.summary ? (
                  <>
                    <div className="brand-main-metric">
                      <span className="metric-value">{formatPercent(brand.competitor.summary.avgImpressionShare)}</span>
                      <span className="metric-label">IS Moyen</span>
                    </div>
                    <div className="brand-metrics">
                      <div className="brand-metric">
                        <span className="label">Abs. Top IS</span>
                        <span className="value">{formatPercent(brand.competitor.summary.avgAbsoluteTopIS)}</span>
                      </div>
                      <div className="brand-metric negative">
                        <span className="label">Perte Rang</span>
                        <span className="value">{formatPercent(brand.competitor.summary.avgRankLostIS)}</span>
                      </div>
                      <div className="brand-metric">
                        <span className="label">Campagnes</span>
                        <span className="value">{brand.competitor.summary.count}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="no-data-text">Aucune campagne concurrents détectée</p>
                )}
              </div>

              {/* Brand trend chart */}
              <div className="brand-trend-chart">
                <h4>Évolution IS Marque</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={prepareBrandTrendData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="period"
                      tickFormatter={(value) => formatPeriodLabel(value, 'month')}
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                    />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      formatter={(value) => value ? `${value.toFixed(1)}%` : '—'}
                      labelFormatter={(value) => formatPeriodLabel(value, 'month')}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="brand"
                      name="Marque"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="competitor"
                      name="Concurrents"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Section 5: Auction Insights par Campagne - Concurrents */}
      {/* MASQUÉ: Nécessite un accès API Standard de Google (authorization_error: 26) */}
      {/* Réactiver quand l'accès aux métriques auction_insight_* sera approuvé */}
      {auctionInsightsByCampaign.length > 0 && (
        <section className="competition-section">
          <div className="section-header" onClick={() => toggleSection('auctionInsights')}>
            <h2><Users size={20} /> Concurrents par Campagne (Auction Insights)</h2>
            {expandedSections.auctionInsights ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>

          {expandedSections.auctionInsights && (
            <div className="section-content">
              <div className="auction-campaigns-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {auctionInsightsByCampaign.map(campaign => (
                  <div
                    key={campaign.id}
                    className="auction-campaign-card"
                    style={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      className="campaign-header"
                      style={{
                        padding: '16px 20px',
                        background: '#f9fafb',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Search size={20} style={{ color: CHANNEL_COLORS.SEARCH }} />
                        <span style={{ fontWeight: '600', fontSize: '1.05em', color: '#1f2937' }}>
                          {campaign.name}
                        </span>
                      </div>
                      <span
                        style={{
                          background: '#dbeafe',
                          color: '#1e40af',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '0.85em',
                          fontWeight: '500'
                        }}
                      >
                        {campaign.competitorCount} concurrent{campaign.competitorCount > 1 ? 's' : ''}
                      </span>
                    </div>

                    <div style={{ padding: '16px 20px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                            <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: '500' }}>Domaine</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: '500' }}>IS</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: '500' }}>Chevauchement</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: '500' }}>Pos. au-dessus</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: '500' }}>Top IS</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: '500' }}>Outranking</th>
                          </tr>
                        </thead>
                        <tbody>
                          {campaign.competitors.slice(0, 10).map((comp, idx) => (
                            <tr
                              key={comp.domain}
                              style={{
                                borderBottom: '1px solid #f3f4f6',
                                backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa'
                              }}
                            >
                              <td style={{ padding: '10px 12px', fontWeight: '500', color: '#374151' }}>
                                {comp.domain}
                              </td>
                              <td style={{ textAlign: 'right', padding: '10px 12px' }}>
                                <span style={{
                                  background: '#dbeafe',
                                  color: '#1e40af',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontWeight: '500'
                                }}>
                                  {formatPercent(comp.impressionShare)}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', padding: '10px 12px', color: '#4b5563' }}>
                                {formatPercent(comp.overlapRate)}
                              </td>
                              <td style={{ textAlign: 'right', padding: '10px 12px', color: '#4b5563' }}>
                                {formatPercent(comp.positionAboveRate)}
                              </td>
                              <td style={{ textAlign: 'right', padding: '10px 12px', color: '#4b5563' }}>
                                {formatPercent(comp.topImpressionShare)}
                              </td>
                              <td style={{ textAlign: 'right', padding: '10px 12px' }}>
                                <span style={{
                                  color: (comp.outrankingShare || 0) > 0.5 ? '#059669' : '#dc2626',
                                  fontWeight: '500'
                                }}>
                                  {formatPercent(comp.outrankingShare)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {campaign.competitors.length > 10 && (
                        <div style={{
                          textAlign: 'center',
                          padding: '12px',
                          color: '#6b7280',
                          fontSize: '0.85em'
                        }}>
                          +{campaign.competitors.length - 10} autres concurrents
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

    </div>
  );
};

export default CompetitionDashboard;
